import { DateTime } from 'luxon';
import { UserLicenseType, NEVER_EXPIRES } from '@cf-shared/data-model/couch-user';
import type { CouchUserPaddleData } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import { isWebBilledPaymentFailing } from '@cf-shared/data-model/subscription-channel-rules';

/**
 * The pure decision behind the MEMBER's billing card on the home screen.
 *
 * Lives outside the component for the same reason `decideCoachBillingCard` next
 * door does: a card-visibility rule with consequences should be unit-testable
 * without standing up Angular.
 *
 * ## What this card is for
 *
 * A member who pays through the web (Paddle) and whose renewal card is declined
 * is NOT cut off straight away. Paddle retries the charge, and through that
 * whole window the membership stays live and the person keeps full access, up
 * to a known end date. That window is the only chance they have to fix the card
 * and keep their membership.
 *
 * Until this card existed, the app said so in exactly one place: a note
 * part-way down the User Profile page. The home screen — where people actually
 * spend their time — said nothing, so somebody could open the app every day,
 * see nothing out of the ordinary, and only find out once it was too late.
 *
 * The terminal loss itself was never silent: when access ends the person lands
 * on the Subscribe paywall, which says the subscription is inactive. What was
 * missing is the WARNING while the problem can still be fixed, which is exactly
 * what this card is.
 *
 * ## Four rules, and one of them is easy to get wrong
 *
 * 1. **Is this the member's OWN web membership?** The licence type must be
 *    `Paddle` specifically — see the next rule for why that is not the same as
 *    "the billing provider works out to Paddle".
 * 2. **Is the payment genuinely failing?** From `isWebBilledPaymentFailing`,
 *    the SAME shared rule the User Profile note and the server both read, so
 *    the card can never claim a payment failed where that rule would not. It
 *    carries two things this rule must not restate: that only Paddle can tell a
 *    declined card apart from a deliberate cancellation (on the app stores both
 *    arrive as the same stored status), and that an already-scheduled CANCEL
 *    suppresses the claim while an already-scheduled PAUSE does not.
 * 3. **Is the window still open?** A `Suspended` block whose expiry is already
 *    spent is not a retry window any more, it is an ended membership — and the
 *    paywall is already telling them so. Quoting a date that has passed as a
 *    future deadline would be simply false.
 * 4. **Is there a date to quote?** Only a block stored as indefinite reaches
 *    this with nothing to quote, and the caller then draws the wording that
 *    names no date. An absent or unreadable expiry never gets here at all — it
 *    fails rule 3, because a window we cannot place in time is one we cannot
 *    honestly claim is still open.
 *
 * ## The coach trap, which is the one to be careful about
 *
 * A coach who pays us per seat through the web holds licence type `CoachSeat`,
 * not `Paddle` — and `licenseTypeToBillingProvider` maps `CoachSeat` onto the
 * `'paddle'` provider. So a rule written as "the mapped provider is Paddle and
 * the status is Suspended" fires for a seat coach in dunning, and that coach
 * already gets `decideCoachBillingCard`'s card about the very same failed
 * payment. The two would stack — which is precisely the double-message #4455
 * merged the two coach cards to remove.
 *
 * That is why rule 1 tests the licence type directly instead of going through
 * the provider mapping, and why there is a test named for it. Anybody
 * "simplifying" this to reuse `licenseTypeToBillingProvider` reintroduces the
 * stacking.
 */
export interface MemberBillingBlocks {
  /** Which rail the reconciler settled on for this person's own membership.
   *  `CouchLoginResult` satisfies this shape structurally, so the caller passes
   *  the login result straight in. */
  licenseType?: UserLicenseType;
  /** The member's own Paddle block. */
  paddleData?: CouchUserPaddleData;
}

export interface MemberBillingCardInput {
  /** The signed-in person's own login result. Null when signed out or offline. */
  member: MemberBillingBlocks | null | undefined;
  /** Evaluation time, passed in so the rule stays pure and testable. */
  now: DateTime;
  /**
   * Whether this person has already dismissed the card in THIS app session.
   * Deliberately not persisted — see `dismissedByUid` below.
   */
  dismissedThisSession: boolean;
}

export interface MemberBillingCardDecision {
  /** Whether to render the card at all. */
  show: boolean;
  /**
   * The date the membership runs to if the payment keeps failing, as stored
   * (UTC ISO). Absent when there is none worth quoting, and the caller then
   * draws the wording that names no date. Only meaningful when `show` is true.
   */
  endDate?: string;
}

const NOT_SHOWING: MemberBillingCardDecision = { show: false };

/**
 * Members who have dismissed the card in this app session, by uid.
 *
 * Module state rather than storage, because the policy is PER SESSION: do not
 * nag somebody twice in one sitting, but do warn them again next time they open
 * the app while their payment is still failing. The retry window is short and
 * what is at stake is their own access, so a member still in trouble tomorrow
 * should be warned again tomorrow — a dismissal that outlived the session could
 * silently cover the whole window, which is the entire problem this card exists
 * to fix.
 *
 * Module state expresses exactly that with nothing to clean up: it lives as long
 * as the JavaScript context, which IS one app session, and any full reload
 * clears it. Keyed by uid so two accounts used in one session do not inherit
 * each other's dismissal. This mirrors the coach billing card's own dismissal,
 * deliberately — see `cs-day-items-coach-cards.ts`.
 */
const dismissedByUid = new Set<string>();

/** Record that this member dismissed the card for the rest of this session. */
export function markMemberBillingCardDismissed(uid: string | undefined): void {
  if (uid) dismissedByUid.add(uid);
}

/** Whether this member has already dismissed the card in this session. */
export function isMemberBillingCardDismissed(uid: string | undefined): boolean {
  return !!uid && dismissedByUid.has(uid);
}

/** Whether to show the member the payment-failing card, and with which date. */
export function decideMemberBillingCard(input: MemberBillingCardInput): MemberBillingCardDecision {
  if (input.dismissedThisSession) return NOT_SHOWING;
  if (!input.member) return NOT_SHOWING;

  // Rule 1 — the person's OWN membership, billed through Paddle. Tested on the
  // licence type directly and NOT through `licenseTypeToBillingProvider`: that
  // maps a coach seat onto Paddle too, and a seat coach already has the coach
  // billing card for this same failed payment. See the header.
  if (input.member.licenseType !== UserLicenseType.Paddle) return NOT_SHOWING;

  const paddle = input.member.paddleData;
  if (!paddle) return NOT_SHOWING;

  // Rule 2 — the one shared rule, not a second copy of it.
  const failing = isWebBilledPaymentFailing(
    'paddle',
    paddle.subscriptionStatus,
    paddle.scheduledChange?.action === 'cancel',
  );
  if (!failing) return NOT_SHOWING;

  // Rule 3 — the retry window is still open. A block whose expiry has passed is
  // an ended membership, and the paywall already says so; telling this person
  // we "will keep trying" would contradict the screen they are about to be sent
  // to.
  //
  // The block's OWN status is passed rather than the `Suspended` the rule above
  // has just established. The two are the same value today, but writing the
  // literal would restate an assumption about `isWebBilledPaymentFailing` —
  // exactly what rule 2 exists to avoid — so if that predicate ever widened,
  // this grace check would go on asking about a status the block no longer has.
  if (!isInGrace(paddle.subscriptionStatus, paddle.expiresDate_utc, input.now.toMillis())) {
    return NOT_SHOWING;
  }

  // Rule 4 — the date to quote. Rule 3 has already ruled out both an ABSENT and
  // an UNREADABLE expiry, because `isInGrace` treats each of those as "does not
  // grant" and returned false above. So the only case left with nothing to
  // quote is `'never'`, the stored form of an indefinite membership — which is
  // not a date and must not be printed as one.
  const expires = paddle.expiresDate_utc;
  return { show: true, endDate: expires === NEVER_EXPIRES ? undefined : expires };
}
